import { useState } from "react";
import Button from "./Button";
import Input from "./Input";
import { useTranslation } from "react-i18next";

interface ResourceOption {
  id: number;
  name: string;
}

interface ResourceItem {
  id: number;
  name: string;
  value: number;
}

interface Props {
  resources: ResourceOption[];
  selectedResources: ResourceItem[];
  setSelectedResources: (items: ResourceItem[]) => void;
  legend?: string;
  disabled?: boolean;
}

export default function ResourceTableSelect({
  resources,
  selectedResources,
  setSelectedResources,
  legend,
  disabled = false,
}: Props) {
  const [selectedId, setSelectedId] = useState("");
  const [quantity, setQuantity] = useState("");
  const { t } = useTranslation();

  const addResource = () => {
    if (!selectedId || !quantity || Number(quantity) <= 0) return;

    if (selectedResources.some((item) => item.id === Number(selectedId))) {
      return;
    }

    const selectedResource = resources.find((r) => r.id === Number(selectedId));
    if (!selectedResource) return;

    const newItem: ResourceItem = {
      id: Number(selectedId),
      name: selectedResource.name,
      value: Number(quantity),
    };

    setSelectedResources([...selectedResources, newItem]);

    // limpar inputs
    setSelectedId("");
    setQuantity("");
  };

  const removeResource = (id: number) => {
    setSelectedResources(selectedResources.filter((item) => item.id !== id));
  };

  // Estilos copiados do Input.tsx para manter consistência no Select
  const selectContainerStyle =
    "px-2 py-1 bg-neutro-50 rounded-sm border w-full relative"; 

  return (
    <fieldset 
      className={`p-4 border rounded-xl bg-white shadow-sm transition-opacity ${
        disabled ? "opacity-60 pointer-events-none" : ""
      }`}
    >
      <legend className="text-lg font-semibold mb-4">{legend}</legend>

      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className="block font-medium text-primario-700 mb-1">
            {t("resourcesForm.inputs.nomeRecurso.label")}
          </label>
          <div className={selectContainerStyle}>
            <select
              disabled={disabled}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full py-1 outline-none bg-transparent"
            >
              <option value="">
                {t("resourcesForm.inputs.nomeRecurso.placeholder")}
              </option>
              {resources.map((r) => {
                const alreadySelected = selectedResources.some(
                  (s) => s.id === r.id
                );
                return (
                  <option key={r.id} value={r.id} disabled={alreadySelected}>
                    {r.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div className="w-1/4">
          <Input
            id="valorRecurso"
            type="number"
            label={t("resourcesForm.inputs.valorRecurso.label")}
            placeholder={t("resourcesForm.inputs.valorRecurso.placeholder")}
            value={quantity}
            onChange={(e: any) => setQuantity(e.target.value)}
            disabled={disabled}
            min="0"
          />
        </div>

        <div className="pb-1">
          <Button
            type="button"
            color="primario"
            label={t("resourcesForm.table.add")}
            classProps="px-6 h-[34px]"
            onClick={addResource}
            disabled={disabled}
          />
        </div>
      </div>

      {selectedResources.length > 0 && (
        <table className="w-full mt-6 border rounded-lg overflow-hidden">
          <thead className="bg-gray-100 text-left text-gray-700">
            <tr>
              <th className="p-3">
                {t("resourcesForm.inputs.nomeRecurso.label")}
              </th>
              <th className="p-3">
                {t("resourcesForm.inputs.valorRecurso.label")}
              </th>
              <th className="p-3">{t("resourcesForm.table.action")}</th>
            </tr>
          </thead>

          <tbody>
            {selectedResources.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="p-3">{item.name}</td>
                <td className="p-3">{item.value}</td>
                <td className="p-3">
                  {!disabled && (
                    <Button
                      type="button"
                      color="red"
                      label={t("resourcesForm.table.remove")}
                      onClick={() => removeResource(item.id)}
                      classProps="px-4 py-1 text-sm"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </fieldset>
  );
}